import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Stack,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import InventoryServices from "../../services/InventoryServices";

const KycReport = () => {
  const [summary, setSummary] = useState({});

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);

  const [pageSize, setPageSize] = useState(50);

  const [rowCount, setRowCount] = useState(0);

  const [previewOpen, setPreviewOpen] = useState(false);
const [previewType, setPreviewType] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  const [previewLoading, setPreviewLoading] = useState(false);

  //--------------------------------------------------
  // Summary
  //--------------------------------------------------

  const loadSummary = async () => {
    try {
        const res = await InventoryServices.kycSummary({});


        setSummary(res.data);
    } catch (err) {
        console.log(err);
    }
};

  //--------------------------------------------------
  // List
  //--------------------------------------------------

  const loadList = useCallback(async () => {
    try {
      setLoading(true);

      const payload = {
        page: page + 1,
        page_size: pageSize,
        search: search,
      };

      const res = await InventoryServices.kycList(payload);
       setRows(res.data.results || []);
    setRowCount(res.data.total || 0);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  //--------------------------------------------------
  // Initial Load
  //--------------------------------------------------

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  //--------------------------------------------------
  // Search
  //--------------------------------------------------

  const handleSearch = () => {
    setPage(0);
    loadList();
  };

  //--------------------------------------------------
  // Reset
  //--------------------------------------------------

  const handleReset = () => {
    setSearch("");
    setPage(0);

    setTimeout(() => {
      loadList();
    }, 100);
  };

  //--------------------------------------------------
  // Excel Export
  //--------------------------------------------------

  const handleExportExcel = async () => {
    try {
      const res = await InventoryServices.exportKycExcel({
        search,
      });

      const url = window.URL.createObjectURL(
        new Blob([res.data])
      );

      const link = document.createElement("a");

      link.href = url;

      link.download = "KYC_Report.xlsx";

      link.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
    }
  };

  //--------------------------------------------------
  // ZIP Export
  //--------------------------------------------------

  const handleExportZip = async () => {
    try {
      const res = await InventoryServices.exportKycZip({
        search,
      });

      const url = window.URL.createObjectURL(
        new Blob([res.data])
      );

      const link = document.createElement("a");

      link.href = url;

      link.download = "KYC_Documents.zip";

      link.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
    }
  };

  //--------------------------------------------------
  // Document Preview
  //--------------------------------------------------

 const handlePreview = async (id) => {
  try {
    setPreviewLoading(true);

    console.log("Opening KYC document for ID:", id);

    const res = await InventoryServices.kycDocument(id);

    console.log("KYC Document Response:", res);
    console.log("Content-Type:", res.headers["content-type"]);

    const contentType =
      res.headers["content-type"] || "application/pdf";

    const blob = new Blob([res.data], {
      type: contentType,
    });

    const url = URL.createObjectURL(blob);

    setPreviewUrl(url);
    setPreviewType(contentType);
    setPreviewOpen(true);

  } catch (err) {
    console.error("KYC Document Error:", err);
  } finally {
    setPreviewLoading(false);
  }
};

  //--------------------------------------------------
  // Grid Columns
  //--------------------------------------------------

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
    },
    {
      field: "uid",
      headerName: "UID",
      minWidth: 180,
      flex: 1,
    },
    {
      field: "imei",
      headerName: "IMEI",
      minWidth: 180,
      flex: 1,
    },
    {
      field: "owner_name",
      headerName: "Owner Name",
      minWidth: 180,
      flex: 1,
    },
    {
      field: "owner_phone",
      headerName: "Owner Phone",
      minWidth: 170,
      flex: 1,
    },
    {
      field: "vehicle_reg_no",
      headerName: "Vehicle No",
      minWidth: 170,
      flex: 1,
    },
    {
      field: "commercial_activation_status",
      headerName: "Commercial Status",
      minWidth: 220,
      flex: 1,
    },
    {
      field: "kyc_updated_at",
      headerName: "KYC Updated At",
      minWidth: 190,
      flex: 1,
    },
    {
    field: "owner_kyc_document_no",
    headerName: "Owner KYC Document No",
    minWidth: 220,
    flex: 1,
  },
  {
    field: "owner_kyc_document",
    headerName: "Owner KYC Document",
    minWidth: 180,
    flex: 1,
    sortable: false,
    renderCell: (params) => (
      <Button
        variant="outlined"
        size="small"
        startIcon={<VisibilityIcon />}
        onClick={() => handlePreview(params.row.id)}
      >
        View
      </Button>
    ),
  },
    // {
    //   field: "doc",
    //   headerName: "Document",
    //   minWidth: 140,
    //   flex: 1,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <Button
    //       variant="outlined"
    //       size="small"
    //       startIcon={<VisibilityIcon />}
    //       onClick={() => handlePreview(params.row.id)}
    //     >
    //       View
    //     </Button>
    //   ),
    // },
  ];
    return (
    <Box p={3}>

      {/* ======================================================
                        SUMMARY SECTION
      ======================================================= */}

      <Typography
        variant="h5"
        fontWeight={700}
        mb={2}
        color="primary"
      >
        Lifecycle Summary
      </Typography>

      <Grid container spacing={2} mb={4}>
        {Object.entries(summary).map(([key, value]) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={key}
          >
            <Card
              elevation={2}
              sx={{
                borderRadius: 2,
                height: "100%",
              }}
            >
              <CardContent>

                <Typography
                  variant="h4"
                  fontWeight={700}
                >
                  {value}
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {key.replaceAll("_", " ")}
                </Typography>

              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ======================================================
                        SEARCH SECTION
      ======================================================= */}

      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
        }}
      >
        <CardContent>

          <Typography
            variant="h6"
            mb={3}
            fontWeight={600}
          >
            Search / Filter
          </Typography>

          <Grid
            container
            spacing={2}
            alignItems="center"
          >

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search IMEI / UID / Owner / Vehicle"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
            </Grid>

            <Grid item>

              <Button
                variant="contained"
                onClick={handleSearch}
              >
                Search
              </Button>

            </Grid>

            <Grid item>

              <Button
                variant="outlined"
                onClick={handleReset}
              >
                Reset
              </Button>

            </Grid>

            <Grid item>

              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
              >
                Export Excel
              </Button>

            </Grid>

            <Grid item>

              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleExportZip}
              >
                Export ZIP (Docs)
              </Button>

            </Grid>

          </Grid>

        </CardContent>
      </Card>

      {/* ======================================================
                        TABLE HEADER
      ======================================================= */}

      <Card
        sx={{
          borderRadius: 2,
        }}
      >
        <CardContent>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >

            <Typography
              variant="h6"
              fontWeight={700}
            >
              KYC Complete ({rowCount} Total)
            </Typography>

          </Stack>
                   <DataGrid
  autoHeight
  rows={rows}
  columns={columns}
  loading={loading}
  pagination
  paginationMode="server"
  rowCount={rowCount}
  page={page}
  pageSize={pageSize}
  rowsPerPageOptions={[25, 50, 100]}
  disableSelectionOnClick
  getRowHeight={() => "auto"}   // <-- ADD THIS
  onPageChange={(newPage) => setPage(newPage)}
  onPageSizeChange={(newSize) => {
    setPageSize(newSize);
    setPage(0);
  }}
  sx={{
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f5f5f5",
      fontWeight: 700,
    },

    "& .MuiDataGrid-cell": {
      display: "flex",
      alignItems: "center",
      whiteSpace: "normal",     // <-- text wraps
      wordBreak: "break-word",
      lineHeight: "20px",
      py: 1,
    },

    "& .MuiDataGrid-cellContent": {
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "unset",
    },

    borderRadius: 2,
  }}
/>

        </CardContent>
      </Card>

      {/* ======================================================
                    DOCUMENT PREVIEW DIALOG
      ====================================================== */}

      <Dialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);

          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl("");
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          KYC Document Preview
        </DialogTitle>

        <DialogContent>

          {previewLoading ? (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight={500}
  >
    <CircularProgress />
  </Box>
) : previewUrl ? (
  <>
    {previewType.includes("application/pdf") ? (
      <iframe
        title="kyc-document"
        src={previewUrl}
        width="100%"
        height="700px"
        style={{
          border: 0,
        }}
      />
    ) : (
      <Box
        display="flex"
        justifyContent="center"
      >
        <img
          src={previewUrl}
          alt="Owner KYC Document"
          style={{
            maxWidth: "100%",
            maxHeight: "700px",
            objectFit: "contain",
          }}
        />
      </Box>
    )}
  </>
) : (
  <Typography>
    No document available.
  </Typography>
)}

        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default KycReport;